import { ArrowRight, CheckCircle2, Languages, MousePointer2, Play, ShieldCheck } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { services, stats } from '../data/site';

export function HomePage() {
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!rootRef.current) return;

    const ctx = gsap.context(() => {
      gsap.from('[data-reveal]', {
        opacity: 0,
        y: 28,
        duration: 0.75,
        stagger: 0.08,
        ease: 'power3.out'
      });
    }, rootRef);

    return () => ctx.revert();
  }, []);

  return (
    <main className="site" ref={rootRef}>
      <nav className="nav">
        <a className="brand" href="/" aria-label="SEO Pictures Home">
          <img src="/seo-pictures-logo.svg" alt="SEO Pictures" />
        </a>
        <div className="navLinks">
          <a href="#services">Leistungen</a>
          <a href="#process">Prozess</a>
          <a href="#faq">FAQ</a>
          <a className="navCta" href="/admin">Admin</a>
        </div>
      </nav>

      <section className="hero">
        <div className="heroCopy">
          <p className="eyebrow" data-reveal>SEO Pictures Rebuild</p>
          <h1 data-reveal>Wir schaffen Sichtbarkeit, die verkauft.</h1>
          <p className="heroText" data-reveal>
            Eine neue High-Performance Website fuer SEO-Pictures.de: mehrsprachig, admin-gesteuert,
            conversionstark und im hochwertigen dunklen Agentur-Stil.
          </p>
          <div className="heroActions" data-reveal>
            <a className="button" href="#services">
              Leistungen ansehen <ArrowRight size={18} />
            </a>
            <a className="button ghost" href="/admin">
              Admin Preview <ShieldCheck size={18} />
            </a>
          </div>
        </div>
        <div className="heroVisual" data-reveal>
          <div className="visualTop">
            <span>SEO</span>
            <span>MEDIA</span>
            <span>TRACKING</span>
          </div>
          <div className="dashboardMock">
            <div className="mockHeader">
              <Play size={18} />
              <span>Performance Cockpit</span>
            </div>
            <div className="bars">
              <i />
              <i />
              <i />
              <i />
              <i />
            </div>
            <div className="mockGrid">
              <span>Pages</span>
              <strong>400+</strong>
              <span>Locales</span>
              <strong>6</strong>
            </div>
          </div>
        </div>
      </section>

      <section className="stats" aria-label="Projektkennzahlen">
        {stats.map((stat) => (
          <div className="stat" key={stat.label} data-reveal>
            <strong>{stat.value}</strong>
            <span>{stat.label}</span>
          </div>
        ))}
      </section>

      <section className="section" id="services">
        <div className="sectionHead" data-reveal>
          <p className="eyebrow">Unsere Leistungen</p>
          <h2>Alles spaeter im Admin steuerbar.</h2>
        </div>
        <div className="serviceList">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <article className="serviceItem" key={service.title} data-reveal>
                <span className="serviceNumber">{String(index + 1).padStart(2, '0')}</span>
                <Icon size={28} />
                <div>
                  <h3>{service.title}</h3>
                  <p>{service.text}</p>
                </div>
                <ArrowRight className="serviceArrow" size={22} />
              </article>
            );
          })}
        </div>
      </section>

      <section className="process" id="process">
        <div data-reveal>
          <p className="eyebrow">Custom Admin</p>
          <h2>Volle Kontrolle ueber Content, SEO und Tracking.</h2>
        </div>
        <div className="processGrid">
          {[
            ['01', 'Content', 'Seiten, Sections, Blog, Portfolio, FAQ, Medien.'],
            ['02', 'SEO', 'Meta, hreflang, Schema, Slugs, Redirects, Sitemap.'],
            ['03', 'Tracking', 'Umami, GA4, Ads, Pixel, Conversion Events.'],
            ['04', 'Sprachen', 'DE, EN, FR, ES, IT und AR mit RTL-Unterstuetzung.']
          ].map(([num, title, text]) => (
            <article className="processCard" key={title} data-reveal>
              <span>{num}</span>
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="faq" id="faq">
        <div data-reveal>
          <p className="eyebrow">Technische Basis</p>
          <h2>Cloudflare, Supabase, R2, Sentry.</h2>
        </div>
        <div className="faqGrid" data-reveal>
          <div><CheckCircle2 /> Cloudflare Pages/Workers fuer Dev und Live</div>
          <div><CheckCircle2 /> R2 Buckets fuer Live Assets und Derivate</div>
          <div><Languages /> Sechs Sprachen inklusive Arabisch</div>
          <div><MousePointer2 /> Conversion Tracking als Admin Settings</div>
        </div>
      </section>

      <section className="finalCta" data-reveal>
        <h2>Naechster Schritt: echte Daten und Admin-Backend verdrahten.</h2>
        <a className="button light" href="/admin">Admin oeffnen <ArrowRight size={18} /></a>
      </section>
    </main>
  );
}

